import React from "react";
import {
  SvgMapInteractionsEventMap,
  UseSvgMapInteractionsReturn,
} from "@gettactic/helpers/src/map/svg/useSvgMapInteractions";
import { useBooleanState } from "../../../../../helpers";
import { ZoomControls } from "../../ZoomControls";

interface SvgMiniMapProps {
  width?: number;
  height?: number;
  mapInteractor: UseSvgMapInteractionsReturn;
  disabled?: boolean;
  embedded?: boolean;
}

const classes = {
  ZoomControlsWrapper:
    "hidden md:flex rounded-md items-center fixed bg-background-primary shadow-sm transition-opacity duration-300 ease-in-out select-none pointer-events-auto z-sticky",
  // Vertical position is set entirely by the inline `bottom` style below —
  // these variants must stay `top`-free: a `fixed` element with both `top`
  // and `bottom` set stretches to fill the gap instead of shrink-to-fit.
  ZoomControlsWrapperEmbedded: "right-2",
  ZoomControlsWrapperNormal: "right-10",
};

export const SvgMiniMap: React.FC<SvgMiniMapProps> = ({
  width: defaultWidth = 200,
  height: defaultHeight = 150,
  mapInteractor,
  disabled = false,
  embedded = false,
}) => {
  // The viewport indicator moves on every pan/zoom frame — written straight
  // to the DOM so the transform stream doesn't re-render the component
  // (which would reconcile the SVG thumbnail and the zoom controls per
  // frame).
  const viewPortRef = React.useRef<HTMLDivElement>(null);

  const isMapReady = useBooleanState();

  const [scaleFactor, setScaleFactor] = React.useState(1);
  const [mapScale, setMapScale] = React.useState(1);
  const [svgContent, setSvgContent] = React.useState<string | null>(null);
  const [svgBBox, setSvgBBox] = React.useState<DOMRect | null>(null);
  const [offset, setOffset] = React.useState({ x: 0, y: 0 });

  React.useEffect(() => {
    if (!isMapReady.state) {
      setSvgContent("");
      return;
    }

    const container = mapInteractor.mapRef.current?.closest(
      ".item-container",
    ) as HTMLElement;
    const svg = container?.querySelector<SVGSVGElement>(".injected-svg");

    if (!container || !svg) return;

    const svgBBox = svg.getBBox();
    setSvgBBox(svgBBox);

    const scaleX = defaultWidth / svgBBox.width;
    const scaleY = defaultHeight / svgBBox.height;
    const scale = Math.min(scaleX, scaleY);

    setScaleFactor(scale);

    // Centering offset
    const offsetX = (defaultWidth - svgBBox.width * scale) / 2;
    const offsetY = (defaultHeight - svgBBox.height * scale) / 2;
    setOffset({ x: offsetX, y: offsetY });

    const clonedSvg = svg.cloneNode(true) as SVGSVGElement;
    clonedSvg.removeAttribute("style");
    // The clone is a static thumbnail — drop the `interactive-{resourceId}`
    // anchor ids so zoom-to-resource and the hover popper (which look them
    // up by id) never resolve to the minimap copy. Other ids stay: defs
    // (gradients, clip paths) are referenced by url(#id) within the clone.
    clonedSvg.querySelectorAll('[id^="interactive-"]').forEach((el) => {
      el.removeAttribute("id");
    });
    clonedSvg.setAttribute("width", `${svgBBox.width * scale}`);
    clonedSvg.setAttribute("height", `${svgBBox.height * scale}`);
    clonedSvg.setAttribute(
      "viewBox",
      `${svgBBox.x} ${svgBBox.y} ${svgBBox.width} ${svgBBox.height}`,
    );
    clonedSvg.setAttribute(
      "style",
      `position: absolute; left: ${offsetX}px; top: ${offsetY}px; display: block;`,
    );
    setSvgContent(clonedSvg.outerHTML);
  }, [
    mapInteractor,
    defaultWidth,
    defaultHeight,
    mapInteractor.mapRef,
    isMapReady.state,
  ]);

  React.useEffect(() => {
    const onTransform = (
      transform: SvgMapInteractionsEventMap["transform"],
    ) => {
      const container = mapInteractor.mapRef.current?.closest(
        ".item-container",
      ) as HTMLElement;
      const svg = mapInteractor.svgRef.current;

      if (!container || !svg || !svgBBox) return;
      // Bails when unchanged (React skips same-value sets), so panning
      // doesn't re-render — only actual zoom changes reach ZoomControls.
      setMapScale(transform.scale);
      const containerRect = container.getBoundingClientRect();

      const visibleWidth = containerRect.width / transform.scale;
      const visibleHeight = containerRect.height / transform.scale;
      const visibleX = -transform.tx / transform.scale;
      const visibleY = -transform.ty / transform.scale;

      const indicator = viewPortRef.current;
      if (indicator) {
        indicator.style.left = `${(visibleX - svgBBox.x) * scaleFactor + offset.x}px`;
        indicator.style.top = `${(visibleY - svgBBox.y) * scaleFactor + offset.y}px`;
        indicator.style.width = `${visibleWidth * scaleFactor}px`;
        indicator.style.height = `${visibleHeight * scaleFactor}px`;
      }
    };

    const onMapReady = () => {
      isMapReady.true();
    };

    const onMapClosed = () => {
      isMapReady.false();
    };

    if (isMapReady.state) {
      mapInteractor.subscribe("transform", onTransform);
    }
    mapInteractor.subscribe("ready", onMapReady);
    mapInteractor.subscribe("closed", onMapClosed);
    return () => {
      mapInteractor.unsubscribe("transform", onTransform);
      mapInteractor.unsubscribe("ready", onMapReady);
      mapInteractor.unsubscribe("closed", onMapClosed);
    };
  }, [mapInteractor, scaleFactor, svgBBox, offset, isMapReady.state]);

  // Memoized so zoom-driven re-renders (scale display) don't re-diff the
  // inlined SVG markup.
  const thumbnail = React.useMemo(
    () =>
      svgContent ? (
        <div
          className="absolute inset-0"
          // biome-ignore lint/security/noDangerouslySetInnerHtml: minimap thumbnail is our own serialized map SVG
          dangerouslySetInnerHTML={{ __html: svgContent }}
        />
      ) : null,
    [svgContent]
  );

  const handleMinimapMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();

    if (!svgBBox || !mapInteractor) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const container = mapInteractor.mapRef.current?.closest(
      ".item-container",
    ) as HTMLElement;

    if (!container) return;

    const scale = mapScale ?? 1;
    const containerRect = container.getBoundingClientRect();

    const moveTo = (clientX: number, clientY: number) => {
      const x = clientX - rect.left - offset.x;
      const y = clientY - rect.top - offset.y;

      const mapPointX = svgBBox.x + x / scaleFactor;
      const mapPointY = svgBBox.y + y / scaleFactor;

      const tx = -mapPointX * scale + containerRect.width / 2;
      const ty = -mapPointY * scale + containerRect.height / 2;

      mapInteractor.setTransform(tx, ty, scale);
    };

    moveTo(event.clientX, event.clientY);

    const handleMouseMove = (moveEvent: MouseEvent) => {
      moveTo(moveEvent.clientX, moveEvent.clientY);
    };

    const handleMouseUp = () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <>
      <div
        className={`${classes.ZoomControlsWrapper} ${embedded ? classes.ZoomControlsWrapperEmbedded : classes.ZoomControlsWrapperNormal}`}
        style={{
          opacity: isMapReady.state ? 1 : 0,
          right: embedded ? "8px" : "20px",
          bottom: "180px",
          left: "auto",
        }}
      >
        <ZoomControls
          scale={mapScale || 1}
          onZoomIn={mapInteractor.zoomIn}
          onZoomOut={mapInteractor.zoomOut}
          onCenter={mapInteractor.centerAndFitMap}
          disabled={disabled}
        />
      </div>
      <div
        className={
          "hidden transition-opacity duration-300 ease-in-out md:block"
        }
        style={{
          // Viewport-anchored like the zoom controls above: the host portals
          // this into the map container, so `absolute` would re-anchor it to
          // that container and shift it.
          position: "fixed",
          bottom: embedded ? "8px" : "20px",
          right: embedded ? "8px" : "20px",
          width: `${defaultWidth}px`,
          height: `${defaultHeight}px`,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          border: "1px solid rgba(0, 0, 0, 0.2)",
          borderRadius: "8px",
          overflow: "hidden",
          pointerEvents: "auto",
          opacity: isMapReady.state ? 1 : 0,
        }}
      >
        {thumbnail}
        <div
          ref={viewPortRef}
          // Position/size come from direct DOM writes in the transform
          // handler — only the static appearance lives here.
          style={{
            position: "absolute",
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            border: "1px solid black",
            pointerEvents: "none",
            left: 0,
            top: 0,
            width: 50,
            height: 50,
          }}
        />
        <div
          className={"absolute inset-0"}
          onMouseDown={!disabled ? handleMinimapMouseDown : undefined}
        />
      </div>
    </>
  );
};
